import localforage from 'localforage';

// Configure localforage
localforage.config({
  driver: localforage.INDEXEDDB,
  name: 'OrderLinkApp',
  version: 1.0,
  storeName: 'app_storage',
  description: 'Storage for Order Link PWA'
});

// Storage keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  SITE_LANGUAGE: 'site_language',
  I18N_LANGUAGE: 'i18nextLng',
  SETTINGS: 'settings',
  SETTINGS_BUTTON_POSITION: 'settingsButtonPosition'
} as const;

// Storage utility class
class StorageManager {
  private migrationKey = 'storage_migrated_to_indexeddb';

  private isInitialized = false;

  private initializationPromise: Promise<void> | null = null;

  /**
   * Initialize storage and perform migration if needed
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._performInitialization();
    return this.initializationPromise;
  }

  private async _performInitialization(): Promise<void> {
    try {
      // Check if migration is needed
      const isMigrated = await localforage.getItem(this.migrationKey);
      
      if (!isMigrated) {
        await this.migrateFromLocalStorage();
        await localforage.setItem(this.migrationKey, true);
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Storage initialization failed:', error);
      // Don't throw error to allow fallback to localStorage
      this.isInitialized = true; // Mark as initialized even if migration failed
    }
  }

  /**
   * Migrate data from localStorage to IndexedDB
   */
  private async migrateFromLocalStorage(): Promise<void> {
    const keysToMigrate = Object.values(STORAGE_KEYS);
    
    const migrationPromises = keysToMigrate.map(async (key) => {
      try {
        const localStorageValue = localStorage.getItem(key);
        if (localStorageValue !== null) {
          // Parse JSON if it's a valid JSON string, otherwise store as string
          let parsedValue;
          try {
            parsedValue = JSON.parse(localStorageValue);
          } catch {
            parsedValue = localStorageValue;
          }
          
          await localforage.setItem(key, parsedValue);
          
          // Also cache the migrated value
          this.cache.set(key, parsedValue);
        }
      } catch (error) {
        console.error(`Failed to migrate ${key}:`, error);
      }
    });

    await Promise.all(migrationPromises);
  }

  /**
   * Get item from storage
   */
  async getItem<T = any>(key: string): Promise<T | null> {
    try {
      // Ensure initialization is complete
      await this.initialize();
      return await localforage.getItem<T>(key);
    } catch (error) {
      console.error(`Failed to get item ${key}:`, error);
      // Fallback to localStorage
      try {
        const fallbackValue = localStorage.getItem(key);
        if (fallbackValue !== null) {
          try {
            return JSON.parse(fallbackValue);
          } catch {
            return fallbackValue as unknown as T;
          }
        }
      } catch (fallbackError) {
        console.error(`Fallback to localStorage also failed for ${key}:`, fallbackError);
      }
      return null;
    }
  }

  /**
   * Set item in storage
   */
  async setItem<T = any>(key: string, value: T): Promise<void> {
    try {
      // Ensure initialization is complete
      await this.initialize();
      await localforage.setItem(key, value);
      
      // Also update localStorage as fallback
      try {
        localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
      } catch (lsError) {
        console.warn(`Failed to update localStorage fallback for ${key}:`, lsError);
      }
    } catch (error) {
      console.error(`Failed to set item ${key}:`, error);
      
      // Fallback to localStorage only
      try {
        localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
      } catch (fallbackError) {
        console.error(`Fallback to localStorage also failed for ${key}:`, fallbackError);
        throw error;
      }
    }
  }

  /**
   * Remove item from storage
   */
  async removeItem(key: string): Promise<void> {
    try {
      await localforage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item ${key}:`, error);
      throw error;
    }
  }

  /**
   * Clear all storage
   */
  async clear(): Promise<void> {
    try {
      await localforage.clear();
    } catch (error) {
      console.error('Failed to clear storage:', error);
      throw error;
    }
  }

  /**
   * Get all keys in storage
   */
  async keys(): Promise<string[]> {
    try {
      return await localforage.keys();
    } catch (error) {
      console.error('Failed to get storage keys:', error);
      return [];
    }
  }

  /**
   * Get storage length
   */
  async length(): Promise<number> {
    try {
      return await localforage.length();
    } catch (error) {
      console.error('Failed to get storage length:', error);
      return 0;
    }
  }

  /**
   * Fallback methods for synchronous access (with caching)
   * These should be used sparingly and only when async is not possible
   */
  private cache = new Map<string, any>();

  /**
   * Sync get with cache fallback
   */
  getItemSync<T = any>(key: string): T | null {
    // First check cache
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }
    
    // Fallback to localStorage for immediate access
    try {
      const localStorageValue = localStorage.getItem(key);
      if (localStorageValue !== null) {
        let parsedValue: T;
        try {
          parsedValue = JSON.parse(localStorageValue);
        } catch {
          parsedValue = localStorageValue as unknown as T;
        }
        
        // Cache the value for future access
        this.cache.set(key, parsedValue);
        return parsedValue;
      }
    } catch (error) {
      console.error(`Failed to get ${key} from localStorage:`, error);
    }
    
    return null;
  }

  /**
   * Sync set with cache and async storage update
   */
  setItemSync<T = any>(key: string, value: T): void {
    this.cache.set(key, value);
    
    // Update IndexedDB asynchronously
    this.setItem(key, value).catch(error => {
      console.error(`Failed to sync ${key} to IndexedDB:`, error);
    });
    
    // Also update localStorage as fallback
    try {
      localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to sync ${key} to localStorage:`, error);
    }
  }

  /**
   * Preload frequently accessed items into cache
   */
  async preloadCache(): Promise<void> {
    const frequentKeys = [
      STORAGE_KEYS.ACCESS_TOKEN,
      STORAGE_KEYS.SITE_LANGUAGE,
      STORAGE_KEYS.I18N_LANGUAGE
    ];

    // Ensure initialization is complete
    await this.initialize();

    const preloadPromises = frequentKeys.map(async (key) => {
      try {
        // Try IndexedDB first
        let value = await localforage.getItem(key);
        
        // If not found in IndexedDB, try localStorage
        if (value === null) {
          const lsValue = localStorage.getItem(key);
          if (lsValue !== null) {
            try {
              value = JSON.parse(lsValue);
            } catch {
              value = lsValue;
            }
          }
        }
        
        if (value !== null) {
          this.cache.set(key, value);
        }
      } catch (error) {
        console.error(`Failed to preload ${key}:`, error);
        // Try localStorage fallback
        try {
          const lsValue = localStorage.getItem(key);
          if (lsValue !== null) {
            let parsedValue;
            try {
              parsedValue = JSON.parse(lsValue);
            } catch {
              parsedValue = lsValue;
            }
            this.cache.set(key, parsedValue);
          }
        } catch (fallbackError) {
          console.error(`Failed to preload ${key} from localStorage:`, fallbackError);
        }
      }
    });

    await Promise.all(preloadPromises);
  }

  /**
   * Check if storage is initialized
   */
  getInitializationStatus(): boolean {
    return this.isInitialized;
  }
}

// Create singleton instance
export const storageManager = new StorageManager();

// Export convenience methods
export const storage = {
  getItem: <T = any>(key: string) => storageManager.getItem<T>(key),
  setItem: <T = any>(key: string, value: T) => storageManager.setItem(key, value),
  removeItem: (key: string) => storageManager.removeItem(key),
  clear: () => storageManager.clear(),
  keys: () => storageManager.keys(),
  length: () => storageManager.length(),
  
  // Sync methods (use sparingly)
  getItemSync: <T = any>(key: string) => storageManager.getItemSync<T>(key),
  setItemSync: <T = any>(key: string, value: T) => storageManager.setItemSync(key, value),
  
  // Initialization
  initialize: () => storageManager.initialize(),
  preloadCache: () => storageManager.preloadCache(),
  
  // Status check
  isInitialized: () => storageManager.getInitializationStatus()
};

export default storage;